import { z } from 'zod';
import { Email, Password } from './auth.js';
/**
 * Link mời khách hàng vào dự án + đơn chờ PM duyệt — APM-CLINV-b,
 * docs/plan-issue-reporter.md §7.
 *
 * Khác link mời thành viên (`project_invite`, APM-1317b): người mở link là người
 * **ngoài** tổ chức, tự tạo tài khoản `CLIENT`, và chỉ được đúng vai "Khách hàng"
 * (`issue:read` + `issue:report`) sau khi PM duyệt.
 */
/** Bảy ngày — cùng con số với link mời dự án và link mời tổ chức. */
export const CLIENT_INVITE_TTL_DAYS = 7;
/**
 * Link đang sống của dự án. Bọc trong `{ invite }` chứ không trả `null` trần —
 * trạng thái thường gặp nhất ("chưa tạo link") phải là một giá trị.
 *
 * `url` xem lại được (token mã hoá bằng `SecretVault`, như link mời tổ chức): PM
 * gửi link cho khách thứ hai mà không phải làm chết link khách thứ nhất đang giữ.
 */
export const ClientInviteState = z.object({
    invite: z
        .object({
        url: z.string(),
        expiresAt: z.iso.datetime(),
        createdAt: z.iso.datetime(),
        /** Số người đã gửi đơn qua link này. */
        acceptedCount: z.number().int().nonnegative(),
    })
        .nullable(),
});
/**
 * Token đi trong **body**, kể cả cho lời gọi chỉ đọc — URL của API bị ghi vào
 * access log của proxy (cùng lý do với `InviteTokenInput`).
 */
export const ClientInviteTokenInput = z.object({
    token: z.string().trim().min(1).max(200),
});
/** Xem trước: link này mời vào dự án nào. Link hỏng ⇒ 404, không có dạng "một nửa". */
export const ClientInvitePreview = z.object({
    orgName: z.string(),
    projectKey: z.string(),
    projectName: z.string(),
});
/** Người chưa có tài khoản đăng ký bằng link — tạo tài khoản `CLIENT` + đơn chờ duyệt. */
export const ClientInviteRegisterInput = z.object({
    token: z.string().trim().min(1).max(200),
    displayName: z.string().trim().min(1, 'Tên hiển thị là bắt buộc').max(200),
    email: Email,
    password: Password,
});
/**
 * Kết cục của lượt xin vào bằng link khi **đã** đăng nhập.
 *
 * - `ok` — đơn mới, chờ PM duyệt.
 * - `pending` — đã gửi đơn trước đó; mở lại link không tạo đơn thứ hai.
 * - `already_member` — đã là khách của dự án.
 * - `staff` — tài khoản này là **nhân sự** của tổ chức: link khách không đổi vai
 *   của nhân sự (đó là việc của `user:manage`).
 * - `demo_account` — tài khoản demo không vào tổ chức thật (APM-DEMO-inv).
 */
export const ClientInviteApplyStatus = z.enum([
    'ok',
    'pending',
    'already_member',
    'staff',
    'demo_account',
]);
export const ClientInviteApplyResult = z.object({
    status: ClientInviteApplyStatus,
    projectName: z.string(),
});
/** Một đơn đang chờ PM duyệt. */
export const ProjectClientRequestItem = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string(),
    createdAt: z.iso.datetime(),
});
/** Từ chối kèm lý do (tuỳ chọn) — khách thấy lý do ở `/portal`. */
export const DeclineClientRequestInput = z.object({
    note: z.string().trim().max(500).optional(),
});
/** Đơn của chính người gọi — dải "đang chờ duyệt" ở `/portal`. */
export const MyClientRequest = z.object({
    id: z.uuid(),
    projectName: z.string(),
    orgName: z.string(),
    status: z.enum(['pending', 'declined']),
    /** Lý do từ chối, chỉ có ở `declined`. */
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
//# sourceMappingURL=client-invite.js.map